import { useCallback, useMemo } from 'react';
import { extent } from 'd3-array';
import { scaleUtc } from 'd3-scale';
import { utcFriday, utcWeek, utcMonth, utcYear } from 'd3-time';
import isThisWeek from 'date-fns/isThisWeek';
import last from 'lodash.last';

import intervals from 'maps/period-types';

export { createTimeScale, useTimeScale };

/**
 * This helper function creates a UTC based D3.js time scale,
 * based on an array of Date objects, an interval and an output
 * range. It configures each type of interval slightly different.
 *
 * @param {array} [dates=[]] An array of Date objects
 * @param {string} [interval=intervals.week.value]
 * @param {array} [range=[0, 1]]
 * @param {boolen} notUseFridayLogic to turn off Friday-based logic
 * @param {boolen} notUseEmptyDateBefore to turn off empty week before
 * @returns {function}
 */
function createTimeScale(
  dates = [],
  interval = intervals.week.value,
  range = [0, 1],
  notUseFridayLogic = false,
  notUseEmptyDateBefore = false
) {
  if (!dates) {
    throw new Error('dates cannot be undefined.');
  }

  if (!Object.keys(intervals).includes(interval)) {
    throw new Error(
      `You should pass createTimeScale a valid interval instead of ${interval}.`
    );
  }

  if (range.length !== 2) {
    throw new Error('You should pass a range with a length of two.');
  }

  const domain = extent(dates);

  const weekIsThisWeek = isThisWeek(last(domain), { weekStartsOn: 6 });

  const scale = scaleUtc().domain(domain).range(range);

  if (notUseFridayLogic && !weekIsThisWeek) return scale;

  // If the data is day-based
  if (interval === intervals.day.value) {
    // Extend the domain to be Friday-based weeks and always including bounding Fridays.
    scale.nice(utcFriday);

    // Reset the domain to start at the Friday before the first date and
    // use the niced Friday from the current domain as the end.
    scale.domain([utcFriday.floor(domain[0]), scale.domain()[1]]);

    // if we do not use friday logic have data from current week -
    if (weekIsThisWeek) {
      scale.domain([domain[0], utcFriday.offset(scale.domain()[1], 0)]);
    }
  }

  // If the data is week-based
  if (interval === intervals.week.value) {
    // Extend the domain to be Friday-based weeks and always including bounding Fridays.
    scale.nice(utcFriday);

    // Reset the domain to only include the ending niced week and start a week earlier.
    scale.domain([
      notUseEmptyDateBefore
        ? utcFriday.floor(domain[0])
        : utcFriday.offset(domain[0], -1),
      scale.domain()[1],
    ]);
  }

  // If the data is month-based
  if (interval === intervals.month.value) {
    // Extend the domain to be month-based.
    scale.nice(utcMonth);

    // Add one week to both ends of the domain
    // for padding.
    scale.domain([
      utcWeek.offset(scale.domain()[0], -1),
      utcWeek.offset(scale.domain()[1], 1),
    ]);
  }

  // If the data is year-based
  if (interval === intervals.year.value) {
    scale.nice(utcYear);

    // If this year-based trend has only a single year of data
    if (domain[0] === domain[1]) {
      // Add a year at the start of the domain for padding reasons
      scale.domain([
        utcYear.offset(scale.domain()[0], -1),
        utcYear.offset(scale.domain()[1], 1),
      ]);
    }
  }

  return scale;
}

/**
 * This hook creates a memoized time scale based on an array of trends.
 *
 * @param {array} trends array of trends
 * @param {string} interval one of day, week, month, year
 * @param {array} range output range
 * @param {boolen} notUseFridayLogic to turn off Friday-based logic
 * @param {boolen} notUseEmptyDateBefore to turn off empty week before
 * @returns {function}
 */
function useTimeScale(
  trends,
  interval,
  range,
  notUseFridayLogic = false,
  notUseEmptyDateBefore = false
) {
  const timestamps = useMemo(() => {
    const toDate = (d) => d.date;
    return trends.flatMap((trend) => trend.data.map(toDate));
  }, [trends]);

  const scale = useCallback(
    createTimeScale(
      timestamps,
      interval,
      range,
      notUseFridayLogic,
      notUseEmptyDateBefore
    ),
    [timestamps, interval, range, notUseFridayLogic, notUseEmptyDateBefore]
  );

  return scale;
}
